/**
 * @file 사내 게시판 좌측 사이드탭
 *
 * 채널 사이드바와 같은 자리·같은 폭을 쓴다 — 두 화면을 오갈 때 목록 위치가 흔들리지 않게.
 * 행마다 구분선을 두지 않고 여백과 hover 로만 나눈다(채널에서 회색 줄을 걷어낸 것과 같은 이유).
 *
 * 폭·토글·저장은 {@link CollapsibleSidebar} 가 맡고, 접혔을 때 무엇을 남길지만 여기서 정한다
 * ({@link BoardRail}).
 */
import React from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import ActionButton from "../../../component/common/ActionButton";
import CollapsibleSidebar from "../../../component/common/CollapsibleSidebar";
import { C } from "../boardShared";

export default function BoardSidebar({
    types, selectedTypeId, onSelectHome, onSelect, isAdmin, onCreateType, onEditType,
}) {
    return (
        <CollapsibleSidebar
            storageKey="board.sidebar"
            width={250}
            expandTitle="게시판 목록 펼치기"
            collapseTitle="게시판 목록 접기"
            renderCollapsed={() => (
                <BoardRail
                    types={types}
                    selectedTypeId={selectedTypeId}
                    onSelectHome={onSelectHome}
                    onSelect={onSelect}
                />
            )}
            sx={{
                // 목록 pane 과 본문 pane 을 가르는 선 하나만 둔다.
                borderRight: `1px solid ${C.border}`,
                bgcolor: C.surface,
            }}
        >
            {/* pt 를 줄였다 — 위에 토글 줄이 하나 생겨 원래 여백(pt:2)을 그대로 두면 제목이 내려앉는다. */}
            <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.3px", color: C.textPrimary }}>
                        사내 게시판
                    </Typography>
                    {/* 게시판 추가는 전체 관리자만 — 아무나 만들면 게시판이 난립한다. */}
                    {isAdmin && (
                        <ActionButton variant="add"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={onCreateType}
                            sx={{ fontSize: 13, py: 0.5 }}
                            label="추가" />
                    )}
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", pt: 1 }}>
                {/* 홈 — 전체 게시판 모아보기(BoardHome 의 제목과 같은 말이어야 한다) */}
                <SidebarRow
                    selected={selectedTypeId == null}
                    onClick={onSelectHome}
                    icon={<HomeRoundedIcon sx={{ fontSize: 15 }} />}
                    label="전체 게시판"
                />

                <Typography sx={{
                    px: 2, pt: 2, pb: 0.5, fontSize: 11, fontWeight: 700,
                    color: C.textMuted, letterSpacing: "0.5px", textTransform: "uppercase",
                }}>
                    게시판
                </Typography>

                {types.map(t => (
                    <SidebarRow
                        key={t.id}
                        selected={String(t.id) === String(selectedTypeId)}
                        onClick={() => onSelect(t.id)}
                        label={t.name}
                        count={t.postCount}
                        dimmed={!t.active}
                        badges={
                            <>
                                {/* 익명·관리자전용·숨김은 목록에서 바로 성격을 알 수 있게 아이콘으로 표시 */}
                                {t.anonymous && (
                                    <Tooltip title="익명 게시판 — 작성자가 표시되지 않습니다">
                                        <VisibilityOffRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                    </Tooltip>
                                )}
                                {t.writeAdminOnly && (
                                    <Tooltip title="글쓰기는 전체 관리자만 가능합니다">
                                        <LockRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                    </Tooltip>
                                )}
                                {!t.active && (
                                    <Tooltip title="숨김 처리된 게시판 (관리자에게만 보입니다)">
                                        <VisibilityOffOutlinedIcon sx={{ fontSize: 13, color: C.warning }} />
                                    </Tooltip>
                                )}
                            </>
                        }
                        action={isAdmin ? (
                            <Tooltip title="게시판 설정">
                                <IconButton size="small"
                                    onClick={(e) => { e.stopPropagation(); onEditType(t); }}
                                    sx={{ p: 0.3 }}>
                                    <SettingsRoundedIcon sx={{ fontSize: 14, color: C.textMuted }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    />
                ))}

                {types.length === 0 && (
                    <Typography sx={{ px: 2, py: 3, fontSize: 12.5, color: C.textMuted, textAlign: "center" }}>
                        게시판이 없습니다
                    </Typography>
                )}
            </Box>
        </CollapsibleSidebar>
    );
}

/**
 * 게시판 성격 아이콘 — 접힘 상태에서 이름을 대신한다.
 *
 * <p>펼침 상태에서 이름 오른쪽에 붙던 익명·관리자전용 표식을 <b>그대로 주 아이콘으로 승격</b>했다.
 * 새 아이콘 체계를 따로 만들면 폈다 접을 때마다 같은 게시판이 다른 그림으로 보인다.
 * 성격이 없는 보통 게시판만 문서 아이콘을 쓴다.
 */
function boardKindIcon(t) {
    if (t.anonymous)      return <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} />;
    if (t.writeAdminOnly) return <LockRoundedIcon sx={{ fontSize: 18 }} />;
    return <ArticleRoundedIcon sx={{ fontSize: 18 }} />;
}

/** Tooltip 문구 — 접히면 이름이 사라지므로 이름과 성격을 한 줄에 같이 준다. */
function boardTooltip(t) {
    const tags = [
        t.anonymous && "익명",
        t.writeAdminOnly && "관리자만 글쓰기",
        !t.active && "숨김",
    ].filter(Boolean);
    return tags.length ? `${t.name} · ${tags.join(" · ")}` : t.name;
}

/**
 * 접힘(48px) 레일.
 *
 * <p>숨기는 것은 <b>이름과 글 수</b>다. 글 수는 어느 게시판인지 고르는 데 쓰는 정보가 아니라
 * 고른 뒤에 보는 정보라, 48px 을 숫자에 내주면 정작 구분에 필요한 아이콘이 눌린다.
 * 설정(⚙) 도 뺐다 — 접힌 폭에서 잘못 눌리면 관리 다이얼로그가 튀어나온다.
 *
 * <p>펼침은 부모(CollapsibleSidebar)가 레일 전체 클릭으로 처리하므로 여기서 부르지 않는다.
 */
function BoardRail({ types, selectedTypeId, onSelectHome, onSelect }) {
    return (
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 0.5, pb: 1 }}>
            <RailItem
                selected={selectedTypeId == null}
                tooltip="전체 게시판"
                icon={<HomeRoundedIcon sx={{ fontSize: 18 }} />}
                onClick={onSelectHome}
            />
            {types.map(t => (
                <RailItem
                    key={t.id}
                    selected={String(t.id) === String(selectedTypeId)}
                    tooltip={boardTooltip(t)}
                    icon={boardKindIcon(t)}
                    dimmed={!t.active}
                    onClick={() => onSelect(t.id)}
                />
            ))}
        </Box>
    );
}

/** 레일 한 칸 — 선택은 펼침과 같은 옅은 배경 + 파란 아이콘으로만 표시한다. */
function RailItem({ selected, tooltip, icon, dimmed, onClick }) {
    return (
        <Tooltip title={tooltip} placement="right">
            <Box
                onClick={onClick}
                sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    height: 34, mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                    color: selected ? C.primary : C.textMuted,
                    bgcolor: selected ? C.primaryBg : "transparent",
                    "&:hover": { bgcolor: selected ? C.primaryBg : C.bg },
                    transition: "background-color 0.12s",
                    opacity: dimmed ? 0.55 : 1,
                }}
            >
                {icon}
            </Box>
        </Tooltip>
    );
}

/**
 * 사이드탭 한 줄 — 선택은 옅은 배경 + 파란 글씨로만 표시한다.
 *
 * <p>좌측 파란 accent bar 는 뺐다. 배경·글자색·칩이 이미 선택을 충분히 말해 주는데 막대까지 세우면
 * 신호가 겹치고, 목록 왼쪽에 세로 띠가 하나 더 생겨 화면이 시끄러워진다.
 */
function SidebarRow({ selected, onClick, icon, label, count, badges, action, dimmed }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                pl: 2, pr: 1.5, py: 1.05, cursor: "pointer",
                bgcolor: selected ? C.primaryBg : "transparent",
                "&:hover": { bgcolor: selected ? C.primaryBg : C.bg },
                transition: "background-color 0.12s",
                opacity: dimmed ? 0.55 : 1,
                // action 버튼은 hover 때만 — 항상 보이면 목록이 아이콘으로 시끄러워진다.
                "& .board-row-action": { opacity: 0, transition: "opacity 0.12s" },
                "&:hover .board-row-action": { opacity: 1 },
            }}
        >
            {icon && (
                <Box sx={{ display: "flex", color: selected ? C.primary : C.textMuted, flexShrink: 0 }}>{icon}</Box>
            )}
            <Typography noWrap sx={{
                flex: 1, minWidth: 0, fontSize: 13,
                fontWeight: selected ? 700 : 600,
                color: selected ? C.primary : "#1e293b",
            }}>
                {label}
            </Typography>
            {badges}
            {count != null && (
                <Chip label={count} size="small" sx={{
                    height: 17, fontSize: 10.5, fontWeight: 700,
                    bgcolor: selected ? C.primaryLight : C.borderLight,
                    color: selected ? C.primaryText : C.textMuted,
                    "& .MuiChip-label": { px: 0.7 },
                }} />
            )}
            {action && <Box className="board-row-action" sx={{ display: "flex", flexShrink: 0 }}>{action}</Box>}
        </Box>
    );
}
